import { RecordValues } from '@wyz/types';
import {
  Accordion,
  Button,
  Col,
  Flex,
  Icon,
  Row,
  SoftBadge,
  Text,
  Tooltip,
} from '@wyz/ui';
import { mapIntoOptionItem, SUPPLIERS_TYPES_IDS } from '@wyz/utils';
import clsx from 'clsx';
import { get, groupBy, sortBy, uniqBy } from 'lodash-es';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useManageBrands } from '~/common/api';
import {
  EditableTextField,
  NumberField,
  SelectField,
  SwitchField,
} from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { SUPPLIERS_TYPES_CODENAME_TO_ID } from '~/common/helpers';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { PRICING_POLICY_SUPPLIER_TYPE_TRANSLATIONS } from '~/common/translators';
import {
  DefaultOptionGroup,
  ISupplierTypeOptionItem,
  SupplierTypeCodeName,
} from '~/common/types';
import {
  RefinementTable,
  RefinementTableBody,
  RefinementTableHeader,
  usePricingPolicyForm,
} from '~/purchase/components';
import { usePurchaseContext } from '~/purchase/context';
import {
  CommonPricingPolicyHelpers,
  QuotePricingPolicyHelpers,
} from '~/purchase/helpers';
import { PP_CUSTOM_CONSTRAINT, PRICING_TYPE_ENUM } from '~/purchase/schemas';
import { IQuotePricingConfigurationStepForm } from '~/purchase/types';

type BrandBadgesProps = {
  selectedBrands: number[];
  brands: Array<DefaultOptionGroup>;
  limit?: number;
};

const SEGMENT_ORDER = ['PREMIUM', 'QUALITY', 'BUDGET'];

const BrandBadges: React.FC<BrandBadgesProps> = ({
  selectedBrands,
  brands,
  limit = 8,
}) => {
  return (
    <Flex className='space-x-1 flex-wrap'>
      {selectedBrands.slice(0, limit).map((brand) => (
        <SoftBadge
          key={brand}
          bg='classic'
          className='fs-xs positive-relative'
          pill
        >
          <span className='align-middle'>
            {brands
              .flatMap((item) => item.options)
              .find((option) => option.value === brand)?.label ?? ''}
          </span>
        </SoftBadge>
      ))}
      {selectedBrands.length > limit && (
        <SoftBadge bg='classic' className='fs-xs positive-relative' pill>
          <span className='align-middle'>{`+${selectedBrands.length - limit}`}</span>
        </SoftBadge>
      )}
    </Flex>
  );
};

function AccordionButton({
  children,
  eventKey,
  isOpened,
}: React.PropsWithChildren<{ eventKey: string; isOpened: boolean }>) {
  const decoratedOnClick = Accordion.useAccordionButton(eventKey);

  return (
    <div
      id={eventKey}
      role='button'
      className='d-flex align-items-center justify-content-between p-2'
      onClick={decoratedOnClick}
    >
      <div className='flex-grow-1'>{children}</div>
      <div>
        <Icon size='md' icon={isOpened ? 'arrow-down' : 'arrow-right'} />
      </div>
    </div>
  );
}

type PolicyItemProps = {
  policyItemIndex: number;
  onDuplicate: (index: number) => void;
  onRemove: (index: number) => void;
  isOpened: boolean;
  parameters: {
    selectedBrandIds: Array<string>;
    featureContext?:
      | typeof FEATURE_CONTEXT.REDISTRIBUTION_PRICING_POLICY_BRANDS
      | typeof FEATURE_CONTEXT.QUOTE_PRICING_POLICY_BRANDS;
  };
  hiddenFields?: {
    supplierTypes?: boolean;
  };
  supplierIds: Array<string>;
  activeSupplierTypeOptions: ISupplierTypeOptionItem[];
};
export const PolicyItem = ({
  policyItemIndex,
  onDuplicate,
  onRemove,
  isOpened,
  parameters,
  hiddenFields,
  supplierIds,
  activeSupplierTypeOptions,
}: PolicyItemProps) => {
  const { state } = usePurchaseContext();

  const pricingPolicyForm = usePricingPolicyForm();
  const { t } = useI18n();
  const methods = useFormContext<IQuotePricingConfigurationStepForm>();
  const watchedItem = useWatch({
    control: methods.control,
    name: `configurations.${policyItemIndex}`,
  });
  const manageBrands = useManageBrands({
    paginate: {
      enabled: true,
      args: {
        brandPublicKeys: parameters.selectedBrandIds,
        customerIds: supplierIds,
        featureContext: parameters.featureContext,
      },
    },
  });
  const manageSelectedBrands = useManageBrands({
    paginate: {
      enabled: true,
      args: {
        brandPublicKeys: [],
      },
    },
  });
  const hasRefinement = QuotePricingPolicyHelpers.hasRefined(watchedItem);
  const selectedBrands = methods.getValues(`configurations.${policyItemIndex}`);
  const brands = Object.entries(
    groupBy(
      sortBy(
        uniqBy(
          [
            ...(manageBrands.paginate.data?.data ?? []),
            ...(manageSelectedBrands.paginate.data?.data.filter((brand) =>
              selectedBrands.brands.includes(brand.id),
            ) ?? []),
          ],
          'id',
        ),
        [
          function (brand) {
            return SEGMENT_ORDER.indexOf(brand.segment.name);
          },
          'name',
        ],
      ),
      (item) => t(item.segment.i18nKey),
    ),
  ).map(([segment, brands]) => ({
    label: segment,
    options: mapIntoOptionItem(brands, 'name', 'id', (item) => ({
      groupLabel: item.segment.name,
    })),
  })) as Array<DefaultOptionGroup>;

  const itemHasError = get(
    methods.formState.errors,
    `configurations.${policyItemIndex}`,
  );

  return (
    <div
      className={clsx('border', {
        'border-neutral-grey-3': !itemHasError,
        'border-red': itemHasError,
      })}
    >
      <AccordionButton
        isOpened={isOpened}
        eventKey={policyItemIndex.toString()}
      >
        <Flex className='w-100' alignItems='center' justifyContent='between'>
          <Flex alignItems='center' className='space-x-2'>
            {itemHasError && (
              <span className='text-red'>
                <Icon icon='information-line' size='xxs' />
              </span>
            )}
            <EditableTextField
              name={`configurations.${policyItemIndex}.name`}
              previewAs={Text}
              previewProps={{ weight: 'semi-bold', heading: 'normal' }}
              maxLength={PP_CUSTOM_CONSTRAINT.BLOC_ITEM_NAME_MAX_LENGTH}
            />
          </Flex>
          {!isOpened && (
            <Flex className='space-x-1 flex-wrap'>
              <BrandBadges
                selectedBrands={methods.getValues(
                  `configurations.${policyItemIndex}.brands`,
                )}
                brands={brands}
              ></BrandBadges>
            </Flex>
          )}
          {pricingPolicyForm.mode === 'WRITE' && (
            <Flex className='space-x-2'>
              <Button
                id={`duplicate-${policyItemIndex}`}
                variant='link'
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(policyItemIndex);
                }}
                className='space-x-2'
                size='sm'
              >
                <Icon icon='duplicate' size='xs' />
                <span>
                  <Trans i18nKey='app.common.button.duplicate' />
                </span>
              </Button>
              <Button
                id={`delete-${policyItemIndex}`}
                variant='link'
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(policyItemIndex);
                }}
                className='space-x-2'
                size='sm'
              >
                <Icon icon='trash' size='xs' />
                <span>
                  <Trans i18nKey='app.common.buttons.remove' />
                </span>
              </Button>
            </Flex>
          )}
        </Flex>
      </AccordionButton>
      <Accordion.Collapse eventKey={policyItemIndex.toString()}>
        <div className='p-2'>
          <Row>
            <Col lg={6} sm={12}>
              <SelectField
                name={`configurations.${policyItemIndex}.brands`}
                placeholder={t(
                  'app.quote.pricing_policy.form.step1.brands_placeholder',
                )}
                label={
                  <Trans i18nKey='app.quote.pricing_policy.form.step1.brands_label' />
                }
                options={brands}
                isLoading={manageBrands.paginate.isLoading}
                isDisabled={manageBrands.paginate.isLoading}
                all={{
                  enabled: true,
                  selectAll: t('app.common.select_all'),
                  unselectAll: t('app.common.clear_all'),
                }}
                size='sm'
                wrapItems
                maxShown={Infinity}
                isMulti
                required
                withCheckboxes
              />
            </Col>
            <Col lg={6} sm={12}>
              <SelectField
                formGroupClassName={clsx({
                  'd-none': hiddenFields?.supplierTypes,
                })}
                name={`configurations.${policyItemIndex}.supplierTypes`}
                label={
                  <Trans i18nKey='app.quote.pricing_policy.form.step1.sources_label' />
                }
                placeholder={t(
                  'app.quote.pricing_policy.form.step1.sources_placeholder',
                )}
                options={activeSupplierTypeOptions}
                all={{
                  enabled: true,
                  selectAll: t('app.common.select_all'),
                  unselectAll: t('app.common.clear_all'),
                }}
                onSelect={(newSelected) => {
                  if (
                    newSelected.some(
                      (item) =>
                        item.id === SUPPLIERS_TYPES_IDS.PLATFORM_DISTRIBUTOR,
                    ) &&
                    (
                      [
                        PRICING_TYPE_ENUM.MARGIN,
                        PRICING_TYPE_ENUM.NET_MARGIN,
                      ] as Array<RecordValues<typeof PRICING_TYPE_ENUM>>
                    ).includes(watchedItem.configurationType)
                  ) {
                    methods.resetField(
                      `configurations.${policyItemIndex}.configurationTypeValue`,
                    );
                    methods.setValue(
                      `configurations.${policyItemIndex}.configurationType`,
                      PRICING_TYPE_ENUM.DISCOUNT_BF,
                    );
                  }
                }}
                size='sm'
                maxShown={4}
                isMulti
                withCheckboxes
                required
              />
            </Col>
          </Row>
          <Row className='mt-4 d-flex align-items-center'>
            <Col sm={4} lg={2} className='pe-0 d-flex align-items-center'>
              <Text heading='normal' weight='bold'>
                <Trans i18nKey='app.quote.pricing_policy.form.step1.pricing_configuration' />
              </Text>
            </Col>
            <Col sm={4} lg={2}>
              <SelectField
                name={`configurations.${policyItemIndex}.configurationType`}
                options={
                  pricingPolicyForm.pricingConfigurationTypes.optionItems
                }
                label=''
                size='sm'
                onSelect={() => {
                  methods.clearErrors(
                    `configurations.${policyItemIndex}.configurationTypeValue`,
                  );
                }}
                formGroupClassName=''
              />
            </Col>
            <Col sm={4} lg={1}>
              {!hasRefinement && (
                <NumberField
                  name={`configurations.${policyItemIndex}.configurationTypeValue`}
                  label=''
                  withKnobs={false}
                  min={0}
                  max={100}
                  placeholder='XX'
                  size='sm'
                  feedbackType='tooltip'
                  // TODO: WCT-4639 buggy currency in default Quote pricing policy
                  sign={QuotePricingPolicyHelpers.getFieldSign(
                    watchedItem.configurationType,
                    state.targetCustomer?.deliveryInformation?.country
                      .currencyIsoCode ?? undefined,
                  )}
                />
              )}
            </Col>
            <Col sm={2} lg={1} className='pe-0 d-flex align-items-center'>
              <Text heading='xs' weight='semi-bold'>
                <Flex>
                  <span>
                    <Trans i18nKey='app.quote.pricing_policy.form.step1.refine_by' />
                  </span>
                  <Tooltip
                    label={
                      <Trans i18nKey='app.quote.pricing_policy.form.step1.refine_by_helper' />
                    }
                  >
                    <Icon icon='information-line' size='xxs' />
                  </Tooltip>
                </Flex>
              </Text>
            </Col>
            <Col className='d-flex align-items-center py-1'>
              <SwitchField
                name={`configurations.${policyItemIndex}.refinementFlags.season`}
                variant='DOUBLE'
                leftLabel=''
                rightLabel={<Trans i18nKey='app.common.product.season' />}
                size='md'
              />
              <SwitchField
                name={`configurations.${policyItemIndex}.refinementFlags.tyreType`}
                rightLabel={
                  <Trans i18nKey='app.common.product.vehicule_type' />
                }
                leftLabel=''
                variant='DOUBLE'
                size='md'
              />
              <SwitchField
                name={`configurations.${policyItemIndex}.refinementFlags.diameter`}
                rightLabel={
                  <Trans i18nKey='app.common.product.refinement_diameter' />
                }
                leftLabel=''
                variant='DOUBLE'
                size='md'
              />
              <SwitchField
                name={`configurations.${policyItemIndex}.refinementFlags.supplierType`}
                rightLabel={
                  <Trans i18nKey='app.quote.pricing_policy.form.step1.refinement.source' />
                }
                leftLabel=''
                variant='DOUBLE'
                formGroupClassName={clsx('mb-2', {
                  'd-none': hiddenFields?.supplierTypes,
                })}
                size='md'
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              {hasRefinement && (
                <>
                  {watchedItem.refinementFlags.supplierType ? (
                    <>
                      {watchedItem.supplierTypes.map((supplierTypeCodename) => (
                        <div
                          key={supplierTypeCodename}
                          className='mt-3 space-y-2'
                        >
                          <Text weight='semi-bold'>
                            <Trans
                              i18nKey={
                                PRICING_POLICY_SUPPLIER_TYPE_TRANSLATIONS[
                                  SUPPLIERS_TYPES_CODENAME_TO_ID[
                                    supplierTypeCodename as SupplierTypeCodeName
                                  ]
                                ]
                              }
                            />
                          </Text>
                          <RefinementTable>
                            <RefinementTableHeader
                              flags={watchedItem.refinementFlags}
                            />
                            <RefinementTableBody
                              policyItemIndex={policyItemIndex}
                              refinementFlags={watchedItem.refinementFlags}
                              supplierTypeCodename={supplierTypeCodename}
                            />
                          </RefinementTable>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className='mt-3'>
                      <RefinementTable>
                        <RefinementTableHeader
                          flags={watchedItem.refinementFlags}
                        />
                        <RefinementTableBody
                          policyItemIndex={policyItemIndex}
                          refinementFlags={watchedItem.refinementFlags}
                          supplierTypeCodename={
                            CommonPricingPolicyHelpers.NO_REFINEMENT_VALUE
                          }
                        />
                      </RefinementTable>
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>
        </div>
      </Accordion.Collapse>
    </div>
  );
};
